/**
 * TechGlowEffect
 * 科技主題 — 3D 光暈 + 掃描線 + 網格背景
 * 僅在 tech 主題下渲染，其他主題自動隱藏
 */
interface TechGlowEffectProps {
  /** 控制強度等級：'subtle'（輕微）| 'normal'（預設）| 'strong'（強烈）*/
  intensity?: 'subtle' | 'normal' | 'strong';
  /** 是否顯示動態脈動環 */
  pulse?: boolean;
  /** 額外 className（定位用，例如 absolute inset-0 overflow-hidden） */
  className?: string;
}

export default function TechGlowEffect({
  intensity = 'normal',
  pulse = false,
  className = 'absolute inset-0 overflow-hidden pointer-events-none',
}: TechGlowEffectProps) {

  const opacityMap = {
    subtle:  { orb1: '0.04', orb2: '0.03', orb3: '0.025', orb4: '0.015', scan: '0.018', grid: '0.012', corner: '0.035' },
    normal:  { orb1: '0.07', orb2: '0.055', orb3: '0.04', orb4: '0.025', scan: '0.025', grid: '0.016', corner: '0.06' },
    strong:  { orb1: '0.11', orb2: '0.08', orb3: '0.065', orb4: '0.04', scan: '0.032', grid: '0.022', corner: '0.09' },
  };
  const op = opacityMap[intensity];

  return (
    <div className={className} aria-hidden="true">

      {/* ── Orb 1：主光源，左上方大光球 ── */}
      <div
        className="absolute rounded-full blur-3xl"
        style={{
          width: '700px', height: '700px',
          top: '-200px', left: '-100px',
          background: `radial-gradient(circle, rgba(45,212,191,${op.orb1}) 0%, rgba(20,184,166,${op.orb2}) 50%, transparent 75%)`,
        }}
      />

      {/* ── Orb 2：右中央中等光球 ── */}
      <div
        className="absolute rounded-full blur-3xl"
        style={{
          width: '500px', height: '500px',
          top: '30%', right: '-80px',
          background: `radial-gradient(circle, rgba(45,212,191,${op.orb2}) 0%, rgba(6,182,212,${op.orb3}) 50%, transparent 70%)`,
        }}
      />

      {/* ── Orb 3：底部中央渲染光 ── */}
      <div
        className="absolute rounded-full blur-3xl"
        style={{
          width: '400px', height: '400px',
          bottom: '-80px', left: '35%',
          transform: 'translateX(-50%)',
          background: `radial-gradient(circle, rgba(20,184,166,${op.orb3}) 0%, rgba(13,148,136,${op.orb4}) 60%, transparent 75%)`,
        }}
      />

      {/* ── Orb 4：小亮點光球（近景，製造 3D 層次） ── */}
      <div
        className="absolute rounded-full blur-2xl"
        style={{
          width: '180px', height: '180px',
          top: '20%', left: '60%',
          background: `radial-gradient(circle, rgba(94,234,212,${op.corner}) 0%, rgba(45,212,191,${op.orb3}) 40%, transparent 70%)`,
        }}
      />

      {/* ── 角落輝光：左上角 ── */}
      <div
        className="absolute top-0 left-0 w-72 h-72 blur-2xl"
        style={{
          background: `radial-gradient(ellipse at top left, rgba(45,212,191,${op.corner}) 0%, transparent 65%)`,
        }}
      />

      {/* ── 角落輝光：右下角 ── */}
      <div
        className="absolute bottom-0 right-0 w-64 h-64 blur-2xl"
        style={{
          background: `radial-gradient(ellipse at bottom right, rgba(20,184,166,${op.corner}) 0%, transparent 65%)`,
        }}
      />

      {/* ── 水平掃描線 ── */}
      <div
        className="absolute inset-0"
        style={{
          background: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 58px,
            rgba(45,212,191,${op.scan}) 58px,
            rgba(45,212,191,${op.scan}) 59px
          )`,
        }}
      />

      {/* ── 垂直掃描線（間距較大，製造網格感） ── */}
      <div
        className="absolute inset-0"
        style={{
          background: `repeating-linear-gradient(
            90deg,
            transparent,
            transparent 118px,
            rgba(45,212,191,${op.grid}) 118px,
            rgba(45,212,191,${op.grid}) 119px
          )`,
        }}
      />

      {/* ── 動態脈動環（選填） ── */}
      {pulse && (
        <>
          <div
            className="absolute rounded-full border border-teal-400/20 animate-ping"
            style={{
              width: '320px', height: '320px',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              animationDuration: '3s',
            }}
          />
          <div
            className="absolute rounded-full border border-teal-300/12 animate-ping"
            style={{
              width: '520px', height: '520px',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              animationDuration: '4.5s',
            }}
          />
        </>
      )}

    </div>
  );
}
